import { Card, Control as ControlType } from '@yleisradio/areena-types';
import classNames from 'classnames';
import React from 'react';
import PlayIcon from 'assets/circlePlayIcon.svg';
import { useTranslation } from 'hooks/useTranslation';
import { isNavigator } from 'components/Controls/Control/typeGuards';
import { useControl } from 'components/Controls/Control/useControl';
import { Link } from 'components/Link';
import { useStartoverDialog } from 'components/StartoverDialog/useStartoverDialog';
import styles from './PlayButton.module.scss';

interface Props {
  card: Card;
  cardViewId: string;
  control: ControlType;
  isOverlay: boolean;
  trackClick: (() => void) | undefined;
}

export const PlayButton: React.FunctionComponent<Props> = ({
  card,
  cardViewId,
  control: controlProp,
  isOverlay,
  trackClick,
}) => {
  const { control, action, executingAction } = useControl({
    control: controlProp,
    cardPointer: card.pointer,
  });
  const t = useTranslation();
  const { playFromStart, openStartoverDialog, startoverDialog } =
    useStartoverDialog({ card, cardViewId, playControl: control });

  if (!control) return null;

  const className = classNames(
    styles.playButton,
    isOverlay && styles.playButtonAsOverlay,
    executingAction && styles.playButtonDisabled
  );
  const label = control.title || t('play');
  const children = <PlayIcon className={styles.icon} />;
  const navigatorPointer =
    !playFromStart && isNavigator(control) ? control.destination : undefined;
  const onClick = !executingAction
    ? () => {
        if (playFromStart) {
          openStartoverDialog();
          return;
        }

        action?.();
        trackClick?.();
      }
    : undefined;

  const playButtonElement = navigatorPointer ? (
    <Link
      aria-label={label}
      className={className}
      onClick={onClick}
      pointer={navigatorPointer}
    >
      {children}
    </Link>
  ) : (
    <button aria-label={label} className={className} onClick={onClick}>
      {children}
    </button>
  );

  return (
    <>
      {playButtonElement}
      {startoverDialog}
    </>
  );
};
